import { useEffect } from 'react';
import { useForm, FormProvider, useFieldArray } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import {
  useStyles,
  ModalSubmitButton,
  ModalCancelButton
} from '../parts/cloudStorage/style/style';
import { Grid, Modal } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { InputGuestField } from '../parts/cloudStorage/InputGuestField';
import { LinkArea } from '../parts/cloudStorage/LinkArea';
import { editShareFileGuest } from '../../apicalls/cloud/shareFile';
import { editSharePictureGuest } from '../../apicalls/cloud/sharePicture';
import { checkResData, formatDatetime } from '../../utils/common';
import type {
  TShareModal,
  TShareList,
  TCurrentGuest
} from '../../types/CloudStorageModal';
import type { TGuest } from '../../types/cloudStorage/CloudFileList';

type Tprops = {
  guest: TGuest;
  editFolderShareModalOpen: boolean;
  setEditGuestModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isFileMode: boolean;
  getSearchedGuestList: () => void;
};

// 共有先を編集
export const EditGuestModal: React.FC<Tprops> = (props: Tprops) => {
  const {
    guest,
    editFolderShareModalOpen,
    setEditGuestModalOpen,
    isFileMode,
    getSearchedGuestList
  } = props;

  const { enqueueSnackbar } = useSnackbar();

  // スタイル読み込み
  const classes = useStyles();

  // hook-formの初期設定
  const shareListDefaultValues: TShareList = {
    name: guest.name,
    email: guest.email,
    expiration_at: formatDatetime(new Date(guest.expiration_at), 'YYYY-MM-DD'),
    uploadable: guest.uploadable
  };
  const useFormDefaultValues: TShareModal = {
    list: [shareListDefaultValues]
  };

  const methods = useForm<TShareModal>({
    defaultValues: useFormDefaultValues
  });

  // 編集モーダルを開く際に初期値を更新する
  useEffect(() => {
    if(editFolderShareModalOpen){
      getSearchedGuestList()
      methods.reset(useFormDefaultValues);
    }
  }, [editFolderShareModalOpen]);

  const { getValues, control } = methods;
  const { update } = useFieldArray({ control, name: 'list' });

  // InputGuestFieldコンポーネントは、新規登録時に複数入力できるようにuseFieldArrayを利用し、
  // mapでループすることを前提として作成しているため、propsにfieldArrayのindexが必要
  // このモーダルでの共有先の編集は１件のみであるため、定数として0を定義
  const fieldArrayIndex = 0;

  const onChangeUploadable = () => {
    const data = getValues('list')[fieldArrayIndex];
    const newUploadable = data.uploadable === true ? false : true;
    const newData = { ...data, uploadable: newUploadable };
    update(fieldArrayIndex, newData);
  };

  const onSubmit = (data: TShareModal) => {
    const idName: string = isFileMode
      ? 'share_file_guest_id'
      : 'share_picture_guest_id';
    const param: Omit<TCurrentGuest, 'url'> = {
      [idName]: guest.id,
      construct_id: guest.construct_id,
      folder_id: guest.folder_id,
      name: data.list[fieldArrayIndex].name,
      email: data.list[fieldArrayIndex].email,
      expiration_at:
        data.list[fieldArrayIndex].expiration_at === ''
          ? null
          : data.list[fieldArrayIndex].expiration_at, // 空文字だとエラーになるため、nullで送信
      uploadable: data.list[fieldArrayIndex].uploadable
    };

    if (isFileMode) {
      // 資料の共有先を編集
      editShareFileGuest(param)
        .then(resData => {
          checkResData(resData, enqueueSnackbar);
        })
        .then(() => {
          getSearchedGuestList();
          setEditGuestModalOpen(false);
        });
    } else {
      // 写真の共有先を編集
      editSharePictureGuest(param)
        .then(resData => {
          checkResData(resData, enqueueSnackbar);
        })
        .then(() => {
          getSearchedGuestList();
          setEditGuestModalOpen(false);
        });
    }
  };

  return (
    <Modal
      open={editFolderShareModalOpen}
      onClose={() => setEditGuestModalOpen(false)}
      aria-labelledby='simple-modal-title'
      aria-describedby='simple-modal-description'
      className={classes.container}
    >
      <FormProvider {...methods}>
        <form
          className={classes.paper}
          onSubmit={methods.handleSubmit(onSubmit)}
        >
          <Grid container>
            {/* タイトル */}
            <Grid item xs={12} className={classes.modalTitle}>
              <h1 style={{ fontSize: 24, textAlign: 'center', margin: 24 }}>
                共有先を編集
              </h1>
            </Grid>

            {/* 入力フィールド */}
            <Grid
              item
              xs={12}
              style={{ padding: 24, maxHeight: '35vh', overflowY: 'auto' }}
            >
              <Grid container justify='center'>
                <InputGuestField
                  index={fieldArrayIndex}
                  onChangeUploadable={onChangeUploadable}
                />
              </Grid>
            </Grid>

            {/* ボタンフィールド */}
            <Grid item xs={12} className={classes.modalFooter}>
              <ModalCancelButton
                variant='contained'
                onClick={() => setEditGuestModalOpen(false)}
              >
                キャンセル
              </ModalCancelButton>

              <ModalSubmitButton variant='contained' type='submit'>
                保存
              </ModalSubmitButton>
            </Grid>

            {/* リンクURLエリア（コピーとメール送信） */}
            <Grid item xs={12} style={{ backgroundColor: '#EFEFEF' }}>
              <Grid container justify='center'>
                <LinkArea
                  id={guest.id}
                  name={guest.name}
                  email={guest.email}
                  url={guest.url}
                  isFileMode={isFileMode}
                />
              </Grid>
            </Grid>

            {/* 右上の×アイコン（モーダル閉じる） */}
            <CloseIcon
              style={{
                cursor: 'pointer',
                position: 'absolute',
                top: 24,
                right: 24
              }}
              fontSize='large'
              onClick={() => setEditGuestModalOpen(false)}
            />
          </Grid>
        </form>
      </FormProvider>
    </Modal>
  );
};
